import React from "react";
import OdooServices from "./OdooServices";
import HeroSection from "../common/HeroSection";
import ServiceSidebar from "../services/ServiceSidebar";
import { 
  ReactFlow, 
  Background, 
  MarkerType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ChartNoAxesColumn,
  CheckCircle,
  CircleCheckBig,
  Code,
  CreditCard,
  Database,
  FileText,
  Link,
  Server,
  Settings,
  Users,
} from "lucide-react";

export default function OdooMigration() {

// Custom hook for responsive sizing
const useResponsiveFlow = () => {
  const [dimensions, setDimensions] = React.useState({
    isMobile: false,
    height: 420
  });

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setDimensions({
        isMobile: width < 768,
        height: width < 768 ? 600 : 420
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

const MigrationFlowDiagram = () => {
  const { isMobile, height } = useResponsiveFlow();
  
  // Define custom node types
  const nodeTypes = {
    processNode: ProcessNode,
    systemNode: SystemNode,
  };

  // Simplified nodes with cleaner layout - desktop version
  const desktopNodes = [
    // Source System
    {
      id: 'source',
      type: 'systemNode',
      position: { x: 100, y: 180 },
      data: { 
        label: 'Source System',
        icon: <Server className="text-red-500" />,
        description: 'Legacy ERP or custom business systems'
      }
    },
    
    // Analysis & Planning
    {
      id: 'analysis',
      type: 'processNode',
      position: { x: 400, y: 80 },
      data: { 
        label: 'Analysis & Planning',
        icon: <Code className="text-blue-500" />,
        description: 'Requirements analysis, data mapping, migration strategy'
      }
    },
    
    // Development & Testing
    {
      id: 'development',
      type: 'processNode',
      position: { x: 700, y: 180 },
      data: { 
        label: 'Development & Testing',
        icon: <Settings className="text-purple-500" />,
        description: 'Data transformation, custom development, validation testing'
      }
    },
    
    // Deployment
    {
      id: 'deployment',
      type: 'processNode',
      position: { x: 1000, y: 80 },
      data: { 
        label: 'Deployment',
        icon: <Database className="text-red-500" />,
        description: 'Training, go-live preparation, final data migration'
      }
    },
    
    // Target System
    {
      id: 'target',
      type: 'systemNode',
      position: { x: 1300, y: 180 },
      data: { 
        label: 'Odoo System',
        icon: <Server className="text-primary" />,
        description: 'Fully operational Odoo implementation'
      }
    },
  ];
  
  // Mobile-optimized nodes arrangement
  const mobileNodes = [
    // Source System
    {
      id: 'source',
      type: 'systemNode',
      position: { x: 150, y: 80 },
      data: { 
        label: 'Source System',
        icon: <Server className="text-red-500" />,
        description: 'Legacy ERP or custom business systems'
      }
    },
    
    // Analysis & Planning
    {
      id: 'analysis',
      type: 'processNode',
      position: { x: 150, y: 200 },
      data: { 
        label: 'Analysis & Planning',
        icon: <Code className="text-blue-500" />,
        description: 'Requirements analysis, data mapping, migration strategy'
      }
    },
    
    // Development & Testing
    {
      id: 'development',
      type: 'processNode',
      position: { x: 150, y: 320 },
      data: { 
        label: 'Development & Testing',
        icon: <Settings className="text-purple-500" />,
        description: 'Data transformation, custom development, validation testing'
      }
    },
    
    // Deployment
    {
      id: 'deployment',
      type: 'processNode',
      position: { x: 150, y: 440 },
      data: { 
        label: 'Deployment',
        icon: <Database className="text-red-500" />,
        description: 'Training, go-live preparation, final data migration'
      }
    },
    
    // Target System
    {
      id: 'target',
      type: 'systemNode',
      position: { x: 150, y: 560 },
      data: { 
        label: 'Odoo System',
        icon: <Server className="text-primary" />,
        description: 'Fully operational Odoo implementation'
      }
    },
  ];

  // Enhanced edges with proper routing - desktop version
  const desktopEdges = [
    // Main data flow path
    {
      id: 'source-analysis',
      source: 'source',
      target: 'analysis',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-1)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#3b82f6',
      },
      label: 'Extract',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'analysis-development',
      source: 'analysis',
      target: 'development',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-2)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#8b5cf6',
      },
      label: 'Transform',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'development-deployment',
      source: 'development',
      target: 'deployment',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-3)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#ef4444',
      },
      label: 'Validate',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'deployment-target',
      source: 'deployment',
      target: 'target',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-4)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#059669',
      },
      label: 'Go Live',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    
    // Direct data bus connection
    {
      id: 'source-target-bus',
      source: 'source',
      target: 'target',
      animated: true,
      type: 'straight',
      style: { stroke: 'url(#gradient-bus)', strokeWidth: 6, strokeDasharray: '5,5', opacity: 0.7 },
      label: 'Data Migration Bus',
      labelStyle: { fill: '#475569', fontWeight: 600, fontSize: 14 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.9, stroke: '#cbd5e1' }
    }
  ];
  
  // Mobile version edges with vertical flow
  const mobileEdges = [
    // Vertical flow connections
    {
      id: 'source-analysis',
      source: 'source',
      target: 'analysis',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-1)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#3b82f6',
      },
      label: 'Extract',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'analysis-development',
      source: 'analysis',
      target: 'development',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-2)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#8b5cf6',
      },
      label: 'Transform',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'development-deployment',
      source: 'development',
      target: 'deployment',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-3)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#ef4444',
      },
      label: 'Validate',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    {
      id: 'deployment-target',
      source: 'deployment',
      target: 'target',
      animated: true,
      type: 'smoothstep',
      style: { stroke: 'url(#gradient-4)', strokeWidth: 3 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#059669',
      },
      label: 'Go Live',
      labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.8, stroke: '#e2e8f0' }
    },
    
    // Direct data bus connection along the side
    {
      id: 'source-target-bus',
      source: 'source',
      target: 'target', 
      type: 'straight',
      animated: true,
      style: { 
        stroke: 'url(#gradient-bus)', 
        strokeWidth: 6, 
        strokeDasharray: '5,5', 
        opacity: 0.7 
      },
      sourceHandle: 'right',
      targetHandle: 'right',
      label: 'Data Migration Bus',
      labelStyle: { fill: '#475569', fontWeight: 600, fontSize: 12 },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: 'white', fillOpacity: 0.9, stroke: '#cbd5e1' }
    }
  ];

  // Choose the appropriate layout based on device type
  const nodes = isMobile ? mobileNodes : desktopNodes;
  const edges = isMobile ? mobileEdges : desktopEdges;
  
  // Custom SVG Defs for gradient effects
  const svgDefs = (
    <svg style={{ position: 'absolute', width: 0, height: 0 }}>
      <defs>
        <linearGradient id="gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
        <linearGradient id="gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
        <linearGradient id="gradient-3" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
        <linearGradient id="gradient-4" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
        <linearGradient id="gradient-bus" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="50%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
    </svg>
  );

  return (
    <div className="w-full bg-white rounded-lg" style={{ height: height }}>
      {svgDefs}
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          defaultViewport={{ 
            x: isMobile ? 40 : 0, 
            y: 0, 
            zoom: isMobile ? 0.9 : 0.7 
          }}
          maxZoom={isMobile ? 0.9 : 0.7}
          minZoom={isMobile ? 0.9 : 0.7}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={false}
          panOnScroll={false}
          zoomOnPinch={false}
          panOnDrag={false}
          interactive="false"
          attributionPosition="hidden"
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
        >
          <Background 
            color="#f1f5f9" 
            variant="dots" 
            gap={20} 
            size={1}
          />
        </ReactFlow>
      </div>
    </div>
  );
};

// Improved process node with cleaner design
const ProcessNode = ({ data }) => {
  return (
    <div className="max-w-[240px] bg-white rounded-lg p-4 shadow-md border border-gray-100" style={{maxWidth: "240px"}}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-blue-50 rounded-full">
          {data.icon}
        </div>
        <span className="font-bold text-gray-800">{data.label}</span>
      </div>
      <p className="text-sm text-gray-600">{data.description}</p>
    </div>
  );
};

// Improved system node with cleaner design
const SystemNode = ({ data }) => {
  return (
    <div className="w-[180px] bg-white rounded-lg p-4 shadow-md border border-gray-100" style={{width: "180px"}}>
      <div className="flex justify-center mb-3">
        <div className="p-3 bg-gray-50 rounded-full">
          {data.icon}
        </div>
      </div>
      <h3 className="text-center font-bold text-gray-800 mb-2">{data.label}</h3>
      <p className="text-xs text-center text-gray-600">{data.description}</p>
    </div>
  );
};

  return (
    <div>
      <HeroSection
        title="Odoo Migration Services"
        breadcrumb={[
          { name: "Services", url: "/services" },
          { name: "Odoo Services", url: "/services/odoo-services" },
          {
            name: "Odoo Migration",
            url: "/services/odoo-services/odoo-migration",
          },
        ]}
        background="/assets/images/career.jpg"
      />
      <div className="bg-white">
        <div className="py-16 px-4 mx-auto container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 px-4">
            {/* Sidebar (OdooServices) */}
            <OdooServices />

            {/* <!-- Content Section --> */}

            <div className="lg:col-span-2">
              <div className="" style={{ opacity: 1, transform: "none" }}>
                <div className="mb-8">
                  <span className="text-primary font-medium text-lg inline-block py-1 px-3 rounded-full bg-primary bg-opacity-10 mb-3">
                    Seamless System Transition
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                    Odoo <span className="text-primary">Migration</span>{" "}
                    Services
                  </h2>
                  <div className="prose prose-lg max-w-none text-gray-600">
                    <p>
                      Transitioning to Odoo from legacy systems or upgrading
                      between Odoo versions requires careful planning and
                      execution to ensure data integrity and business
                      continuity. Our migration services provide a structured,
                      methodical approach to ensure your critical business data
                      and processes are seamlessly transferred.
                    </p>
                    <p>
                      At{" "}
                      <span className="text-primary font-medium">
                        TISSA Technology
                      </span>
                      , we specialize in complex migration projects, handling
                      everything from data extraction and transformation to
                      validation and system cutover. Our proven migration
                      methodology minimizes business disruption while maximizing
                      the benefits of your new Odoo implementation.
                    </p>
                    <p>
                      Whether you're moving from an outdated ERP system,
                      consolidating multiple applications, or upgrading your
                      existing Odoo installation, our migration experts ensure a
                      smooth transition that preserves your valuable data and
                      customizations.
                    </p>
                  </div>
                </div>
               <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Odoo Migration Process</h3>
                  <div className="bg-white p-1 rounded-xl shadow-soft border border-gray-100">
                    {/* <MigrationFlowVisualization /> */}
                     <div className="w-full max-w-[1200px] mx-auto" style={{maxWidth: "1200px"}}>
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
        <MigrationFlowDiagram />
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-red-50 rounded-full">
              <Database className="h-5 w-5 text-red-500" />
            </div>
            <h4 className="font-semibold text-gray-800">Data Integrity</h4>
          </div>
          <p className="text-sm text-gray-600">Complete data validation ensures accuracy and completeness</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-blue-50 rounded-full">
              <Code className="h-5 w-5 text-blue-500" />
            </div>
            <h4 className="font-semibold text-gray-800">Custom Processes</h4>
          </div>
          <p className="text-sm text-gray-600">Your unique business processes and customizations preserved</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-purple-50 rounded-full">
              <Settings className="h-5 w-5 text-purple-500" />
            </div>
            <h4 className="font-semibold text-gray-800">Minimal Disruption</h4>
          </div>
          <p className="text-sm text-gray-600">Phased approach minimizes impact on business operations</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-green-50 rounded-full">
              <Users className="h-5 w-5 text-green-500" />
            </div>
            <h4 className="font-semibold text-gray-800">User Adoption</h4>
          </div>
          <p className="text-sm text-gray-600">Comprehensive training ensures seamless transition</p>
        </div>
      </div>
    </div>
                  </div>
                </div>
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">
                    Our Migration Services
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                        <Server className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">
                        Legacy System Migration
                      </h4>
                      <p className="text-gray-600">
                        Transition from outdated systems to a modern Odoo
                        environment, preserving your critical data and business
                        processes while gaining new capabilities.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                        <Database className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">
                        Data Migration
                      </h4>
                      <p className="text-gray-600">
                        Securely transfer and transform your business data to
                        Odoo with our proven methodologies that ensure data
                        integrity and validation.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                        <Code className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">
                        Custom Module Migration
                      </h4>
                      <p className="text-gray-600">
                        Migrate your custom functionalities to Odoo, adapting
                        your unique business processes to work seamlessly in the
                        new environment.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">
                        Odoo Version Upgrades
                      </h4>
                      <p className="text-gray-600">
                        Upgrade from older Odoo versions to the latest release,
                        ensuring compatibility with your customizations and
                        seamless data migration.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                        <Settings className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-2">
                        System Consolidation
                      </h4>
                      <p className="text-gray-600">
                        Consolidate multiple standalone systems into a unified
                        Odoo platform for improved efficiency and centralized
                        data management.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">
                    Our Migration Methodology
                  </h3>
                  <div className="relative">
                    <div className="absolute left-6 top-0 bottom-0 w-1 bg-primary bg-opacity-20 ml-0.5"></div>
                    <div className="space-y-8">
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          1
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Assessment &amp; Analysis
                        </h4>
                        <p className="text-gray-600">
                          We conduct a thorough analysis of your current
                          systems, data structures, and business processes to
                          develop a comprehensive migration strategy.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          2
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Migration Planning
                        </h4>
                        <p className="text-gray-600">
                          Our team creates a detailed migration plan outlining
                          the data mapping, transformation rules, and
                          implementation approach with clear milestones.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          3
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Test Environment Setup
                        </h4>
                        <p className="text-gray-600">
                          We set up testing environments to validate migration
                          procedures and ensure data integrity before making any
                          changes to production systems.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          4
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Data Migration &amp; Customization
                        </h4>
                        <p className="text-gray-600">
                          We execute the data migration following our proven
                          methodology while adapting and migrating custom
                          functionalities to the new environment.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          5
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Validation &amp; Testing
                        </h4>
                        <p className="text-gray-600">
                          Comprehensive testing is performed to ensure data
                          accuracy, system functionality, and performance in the
                          new Odoo environment.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          6
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          User Training
                        </h4>
                        <p className="text-gray-600">
                          We provide thorough training to ensure your team can
                          effectively use the new Odoo system and understand any
                          changes to workflows.
                        </p>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black font-bold">
                          7
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          Go-Live &amp; Transition Support
                        </h4>
                        <p className="text-gray-600">
                          Our team manages the cutover process and provides
                          intensive post-migration support to ensure a smooth
                          transition to the new system.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">
                    Benefits of Our Migration Approach
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-primary">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />

                        <span className="text-gray-700">
                          Complete data integrity with comprehensive validation
                          processes
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Preservation of custom business logic and workflows
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Minimal disruption to ongoing business operations
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Phased implementation approach to reduce transition
                          risks
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Advanced data cleansing and transformation during
                          migration
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Thorough testing to ensure system reliability
                          post-migration
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Comprehensive user training to ensure smooth adoption
                        </span>
                      </div>
                      <div className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">
                          Ongoing support through the transition period and
                          beyond
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">
                    Success Story
                  </h3>
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
                    <h4 className="text-xl font-bold text-primary mb-4">
                      Manufacturing Company Migration
                    </h4>
                    <p className="text-gray-600 mb-4">
                      A mid-sized manufacturing company was struggling with an
                      outdated, heavily customized ERP system that was becoming
                      increasingly difficult to maintain and couldn't scale with
                      their growth. They needed to migrate to a modern solution
                      without losing years of critical business data and custom
                      processes.
                    </p>
                    <p className="text-gray-600 mb-4">
                      We implemented a phased migration to Odoo, carefully
                      mapping their complex data structures, adapting custom
                      functionality, and integrating with their existing
                      warehouse management and production systems. The migration
                      was executed with minimal disruption to their daily
                      operations.
                    </p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-primary font-bold text-2xl">
                          99.8%
                        </div>
                        <div className="text-gray-600">
                          Data Migration Accuracy
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-primary font-bold text-2xl">
                          40%
                        </div>
                        <div className="text-gray-600">
                          Increase in System Performance
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-primary font-bold text-2xl">
                          60%
                        </div>
                        <div className="text-gray-600">
                          Reduction in IT Maintenance Costs
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-100">
                    <h4 className="text-2xl font-bold text-gray-800 mb-3">
                      Ready to Migrate to Odoo?
                    </h4>
                    <p className="text-gray-600 mb-6">
                      Contact our migration specialists today to discuss how we
                      can help you transition to Odoo with minimal disruption
                      and maximum benefit for your business.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      className="inline-block text-blue-700 font-bold bg-primary hover:bg-primary-dark font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                      to="/contact-us/"
                    >
                      Request a Free Consultation
                    </Link>
                    <Link
                      className="inline-block border border-primary text-primary hover:bg-blue-700 hover:text-white font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                      to="/services/odoo-services/"
                    >
                      Explore All Odoo Services
                    </Link>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How long does an Odoo integration project typically take?
                </h3>
                <p className="text-gray-600">
                  The timeline for an Odoo integration project depends on the
                  complexity of the systems being integrated and the scope of
                  the project. Simple integrations can be completed in 2-4
                  weeks, while more complex integrations may take 8-12 weeks.
                  During our initial consultation, we'll provide you with a
                  specific timeline based on your requirements.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Can Odoo be integrated with custom-built systems?
                </h3>
                <p className="text-gray-600">
                  Yes, Odoo can be integrated with custom-built systems. Our
                  team has extensive experience developing custom APIs and
                  connectors to integrate Odoo with proprietary and custom-built
                  systems. We analyze your custom system's architecture and
                  develop appropriate integration solutions to ensure seamless
                  data flow.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How do you ensure data security during integration?
                </h3>
                <p className="text-gray-600">
                  Data security is our top priority. We implement
                  industry-standard encryption protocols, secure API
                  connections, and access controls to protect data during
                  integration. We also conduct thorough security testing before
                  deployment and can sign NDAs to ensure confidentiality.
                  Additionally, we follow best practices for handling sensitive
                  information and comply with relevant data protection
                  regulations.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Will my existing data be migrated during integration?
                </h3>
                <p className="text-gray-600">
                  Yes, data migration is typically part of the integration
                  process. We develop data mapping strategies to ensure accurate
                  data transfer between systems. Our team performs thorough
                  validation to ensure data integrity during migration. We can
                  also implement historical data migration or start with current
                  data only, depending on your requirements.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Do you provide support after the integration is complete?
                </h3>
                <p className="text-gray-600">
                  Absolutely. We offer post-integration support and maintenance
                  services to ensure your integrated systems continue to operate
                  smoothly. Our support packages include regular monitoring,
                  troubleshooting, updates, and optimization services. We also
                  provide training for your team to help them effectively manage
                  the integrated systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
